/**
 * Casos Page - Editorial Style with Dynamic Content
 * Projects loaded from CMS API with gallery support
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, LayoutGrid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { PageWrapper } from '../components/EditorialShared';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Fallback projects (used if API fails)
const fallbackProjects = [
  { 
    project_id: "1", 
    name: "Café Fantasía", 
    category: "Branding",
    description: "Creando un universo visual para una cafetería artesanal que mezcla fantasía con sabor local.",
    services: ["Identidad Visual", "Diseño de Logo", "Aplicaciones", "Fotografía"],
    image: "https://drive.google.com/thumbnail?id=15aukMIPeeTfvtBI1U10fCgSgB9l5HK2H&sz=w1200",
    gallery: [],
    year: "2024"
  },
  { 
    project_id: "2", 
    name: "Andrea Gamoneda", 
    category: "Marca Personal",
    description: "Construyendo una identidad profesional y cálida para una coach de recuperación emocional.",
    services: ["Branding", "Identidad Visual", "Redes Sociales"],
    image: "https://drive.google.com/thumbnail?id=1XXpbb0yGE5vGapKbg05lewXLiPhfseoL&sz=w1200",
    gallery: [],
    year: "2024"
  },
  { 
    project_id: "3", 
    name: "Altitud Pádel", 
    category: "Rebranding",
    description: "Elevando la identidad de un club de pádel con energía, dinamismo y presencia premium.",
    services: ["Rebranding", "Diseño Gráfico", "Social Media"],
    image: "https://drive.google.com/thumbnail?id=1lv0noT1zD56g7FEtuEI_ZT2XcrgO6yoP&sz=w1200",
    gallery: [],
    year: "2024"
  },
  { 
    project_id: "4", 
    name: "El Exquisito", 
    category: "Branding & Fotografía",
    description: "Capturando la esencia de la cocina mixe tradicional en una taquería moderna y auténtica.",
    services: ["Branding", "Fotografía Gastronómica", "Menú Design"],
    image: "https://drive.google.com/thumbnail?id=1oCpgmt7rXyl4yFboo8GrmZyox3jgbPK-&sz=w1200",
    gallery: [],
    year: "2024"
  },
  { 
    project_id: "5", 
    name: "Comic Cafe 2D", 
    category: "Branding",
    description: "Dando vida a un café temático donde el anime y el café se encuentran en cada detalle.",
    services: ["Identidad Visual", "Ilustración", "Diseño de Experiencia"],
    image: "https://drive.google.com/thumbnail?id=1zbthWuHnoAle7l4o69uzNGoa7vnikhRq&sz=w1200",
    gallery: [],
    year: "2024"
  },
  { 
    project_id: "6", 
    name: "Torii Sushi", 
    category: "Branding",
    description: "Fusionando tradición japonesa con creatividad contemporánea en una experiencia gastronómica única.",
    services: ["Branding", "Diseño de Menú", "Fotografía"],
    image: "https://drive.google.com/thumbnail?id=1qDGeNYMWsXTpAARN_D0sH7tJPwQS5ddx&sz=w1200",
    gallery: [],
    year: "2023"
  },
  { 
    project_id: "7", 
    name: "Cocktail Bar", 
    category: "Branding",
    description: "Destilando sofisticación y personalidad en la identidad de un bar de autor.",
    services: ["Identidad Visual", "Diseño de Carta", "Fotografía"],
    image: "https://drive.google.com/thumbnail?id=1WajCZ48vXJCj9RjhfoF0CYBwEs6YlHt7&sz=w1200",
    gallery: [],
    year: "2023"
  },
  { 
    project_id: "8", 
    name: "The Clutch", 
    category: "Branding",
    description: "Definiendo el estilo contemporáneo de una marca de accesorios con actitud.",
    services: ["Branding", "Packaging", "E-commerce"],
    image: "https://drive.google.com/thumbnail?id=1Jgm36umwa0oBSExa8cAsZjKVGli76GaU&sz=w1200",
    gallery: [],
    year: "2023"
  },
];

// View toggle
const ViewToggle = ({ view, setView }) => (
  <div className="flex items-center gap-2 bg-white/5 rounded-full p-1">
    <button
      onClick={() => setView('grid')}
      className={`p-2 rounded-full transition-all ${view === 'grid' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
    >
      <LayoutGrid className="w-5 h-5" />
    </button>
    <button
      onClick={() => setView('index')}
      className={`p-2 rounded-full transition-all ${view === 'index' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'}`}
    >
      <List className="w-5 h-5" />
    </button>
  </div>
);

// Index row
const IndexRow = ({ project, index, onClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="border-b border-white/10 cursor-pointer group py-6"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      onClick={() => onClick(project)}
      data-cursor="ver"
    >
      <div className="grid grid-cols-12 gap-4 items-center">
        <div className="col-span-12 md:col-span-3">
          <h3 className="text-white text-xl font-medium group-hover:opacity-60 transition-opacity">
            {project.name}
          </h3>
        </div>
        <div className="col-span-12 md:col-span-5">
          <p className="text-white/50">{project.description}</p>
        </div>
        <div className="col-span-6 md:col-span-2">
          <span className="text-white/40 text-sm uppercase tracking-wider">{project.category}</span>
        </div>
        <div className="col-span-6 md:col-span-2 text-right">
          <span className="text-white/40 text-sm">{project.year}</span>
        </div>
      </div>
    </motion.div>
  );
};

// Grid card
const GridCard = ({ project, index, onClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="cursor-pointer group"
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      onClick={() => onClick(project)}
      data-cursor="ver"
    >
      <div className="aspect-[4/3] overflow-hidden mb-4">
        <img
          src={project.image}
          alt={project.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      <h3 className="text-white text-xl font-medium mb-1">{project.name}</h3>
      <p className="text-white/50">{project.description}</p>
    </motion.div>
  );
};

// Gallery component for project modal
const ProjectGallery = ({ images, mainImage, projectName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Combine main image with gallery images
  const allImages = [
    { url: mainImage, alt: projectName },
    ...(images || [])
  ];
  
  const goToPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  if (allImages.length <= 1) {
    return (
      <div className="w-full h-[70vh]">
        <img 
          src={mainImage} 
          alt={projectName}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Main image display */}
      <div className="w-full h-[70vh] relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img 
            key={currentIndex}
            src={allImages[currentIndex]?.url}
            alt={allImages[currentIndex]?.alt || projectName}
            className="w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        </AnimatePresence>
        
        {/* Navigation arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        {/* Image counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full">
          <span className="text-white text-sm">
            {currentIndex + 1} / {allImages.length}
          </span>
        </div>
      </div>
      
      {/* Thumbnail strip */}
      <div className="bg-black py-4 px-6">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {allImages.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-20 h-20 overflow-hidden transition-all ${
                currentIndex === index 
                  ? 'ring-2 ring-white opacity-100' 
                  : 'opacity-50 hover:opacity-80'
              }`}
            >
              <img 
                src={img.url} 
                alt={img.alt || `Imagen ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Project modal with gallery
const ProjectModal = ({ project, onClose }) => {
  if (!project) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-[#0a0a0a] overflow-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <button
        onClick={onClose}
        className="fixed top-6 right-6 z-10 text-white/60 hover:text-white transition-colors"
      >
        <X className="w-8 h-8" />
      </button>

      <button
        onClick={onClose}
        className="fixed top-6 left-6 z-10 text-white/60 hover:text-white transition-colors text-sm uppercase tracking-[0.2em]"
      >
        ← Volver
      </button>

      <div className="min-h-screen pt-24 pb-20">
        {/* Gallery or single image */}
        <div className="mb-16">
          <ProjectGallery 
            images={project.gallery} 
            mainImage={project.image}
            projectName={project.name}
          />
        </div>

        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-8">
              <span className="text-white/40 text-sm uppercase tracking-[0.2em] block mb-4">
                {project.category}
              </span>
              <h1 className="text-white text-5xl md:text-7xl font-extralight mb-8">
                {project.name}
              </h1>
              <p className="text-white/60 text-xl font-light leading-relaxed">
                {project.description}
              </p>
            </div>

            <div className="md:col-span-4">
              <div className="space-y-8">
                <div>
                  <h4 className="text-white/40 text-xs uppercase tracking-wider mb-3">Año</h4>
                  <p className="text-white text-lg">{project.year}</p>
                </div>
                <div>
                  <h4 className="text-white/40 text-xs uppercase tracking-wider mb-3">Servicios</h4>
                  <ul className="space-y-2">
                    {project.services?.map((service, i) => (
                      <li key={i} className="text-white/70">{service}</li>
                    ))}
                  </ul>
                </div>
                {project.gallery && project.gallery.length > 0 && (
                  <div>
                    <h4 className="text-white/40 text-xs uppercase tracking-wider mb-3">Galería</h4>
                    <p className="text-white/70">{project.gallery.length + 1} imágenes</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Main component
const EditorialCasosPage = () => {
  const [projects, setProjects] = useState(fallbackProjects);
  const [selectedProject, setSelectedProject] = useState(null);
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const titleRef = useRef(null);
  const isInView = useInView(titleRef, { once: true });

  // Load projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Try portfolio API first (same as admin)
        const response = await fetch(`${API_URL}/api/portfolio/projects`);
        if (response.ok) {
          const data = await response.json();
          if (data.projects && data.projects.length > 0) {
            // Transform portfolio projects to match expected format
            const transformed = data.projects.map(p => ({
              project_id: p.project_id || p.id,
              name: p.name,
              category: p.category_name || p.category,
              description: p.description || '',
              services: p.services || [],
              image: p.images?.[0] || p.image,
              images: p.images || [],
              gallery: (p.images || []).slice(1).map((url, i) => ({
                url,
                alt: `${p.name} - Imagen ${i + 2}`
              })),
              year: p.year || new Date().getFullYear().toString()
            }));
            setProjects(transformed);
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  return (
    <PageWrapper showLogoFooter={true}>
      <div className="pt-32 pb-20">
        {/* Header */}
        <div className="max-w-6xl mx-auto px-6 mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8" ref={titleRef}>
            <div>
              <motion.h1
                className="text-white text-5xl md:text-7xl font-extralight"
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8 }}
              >
                Nuestro trabajo
              </motion.h1>
              <motion.p
                className="text-white/50 text-lg mt-4 max-w-2xl"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: 0.3 }}
              >
                Durante 5 años hemos colaborado con marcas que buscan diferenciarse.
              </motion.p>
            </div>
            <ViewToggle view={view} setView={setView} />
          </div>
        </div>

        {/* Index headers */}
        {view === 'index' && (
          <div className="max-w-6xl mx-auto px-6 mb-4">
            <div className="grid grid-cols-12 gap-4 text-white/30 text-xs uppercase tracking-wider border-b border-white/10 pb-4">
              <div className="col-span-3 hidden md:block">Marca</div>
              <div className="col-span-5 hidden md:block">Proyecto</div>
              <div className="col-span-2 hidden md:block">Categoría</div>
              <div className="col-span-2 text-right hidden md:block">Año</div>
            </div>
          </div>
        )}

        {/* Projects */}
        <div className="max-w-6xl mx-auto px-6">
          <AnimatePresence mode="wait">
            {view === 'grid' ? (
              <motion.div
                key="grid"
                className="grid grid-cols-1 md:grid-cols-2 gap-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {projects.map((project, index) => (
                  <GridCard 
                    key={project.project_id || project.id || index} 
                    project={project} 
                    index={index}
                    onClick={setSelectedProject}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="index"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {projects.map((project, index) => (
                  <IndexRow 
                    key={project.project_id || project.id || index} 
                    project={project} 
                    index={index}
                    onClick={setSelectedProject}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal 
            project={selectedProject} 
            onClose={() => setSelectedProject(null)} 
          />
        )}
      </AnimatePresence>
    </PageWrapper>
  );
};

export default EditorialCasosPage;
